import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  PlusCircle,
  ChevronUp,
  ChevronDown,
  Edit2,
  Trash2,
  Wifi,
  WifiOff,
  AlertTriangle,
  Timer
} from "lucide-react";
import { useDispositivos } from "../../../hooks/useDispositivos";
import "../../../styles/desktop/dispositivo.css";
import { checkAdmin } from "../../../API/server/adminVerify";

const EMPTY_FORM = { name: "", ip: "", description: "", active: true };

const ipv4Regex =
  /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/;
const hostnameRegex =
  /^(?=.{1,253}$)(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/i;

const formatAddress = (value) => {
  // hostname mode ─ contém ao menos uma letra
  if (/[A-Za-z]/.test(value)) {
    return value.replace(/[^A-Za-z0-9.-]/g, "").slice(0, 253);
  }

  // ipv4 mode ─ só dígitos e pontos
  const digits = value.replace(/[^0-9.]/g, "");
  let parts = digits.split(".").slice(0, 4);
  parts = parts.map((p) => p.slice(0, 3));
  let formatted = parts.join(".");
  if (digits.endsWith(".") && !formatted.endsWith(".") && parts.length < 4) {
    formatted += ".";
  }
  return formatted;
};

const isValidAddress = (addr) => {
  const clean = addr.trim();
  return ipv4Regex.test(clean) || hostnameRegex.test(clean);
};

// ---- Linha memoizada: só re-renderiza quando o dispositivo mudar ----
const DeviceRow = React.memo(function DeviceRow({ d, onEdit, onDelete, getDeviceStatus, isAdmin }) {
  const { icon, label, cls } = getDeviceStatus(d);
  const latency = d.last_ping?.response_time;

  return (
    <tr>
      <td>{d.name}</td>
      <td><code className="ip-badge">{d.ip}</code></td>
      <td className={!d.description ? "muted" : undefined}>
        {d.description || "Sem descrição"}
      </td>
      <td>
        <span className={`status-badge ${cls}`}>
          {icon}
          {label}
        </span>
      </td>
      <td className="latency">
        {latency != null ? (
          <>
            <Timer size={14} /> {latency}ms
          </>
        ) : "—"}
      </td>
      <td className={`actions-col ${!isAdmin ? 'hidden-actions' : ''}`}>
        {isAdmin && (
          <>
            <button className="icon-btn" onClick={() => onEdit(d)} title="Editar"><Edit2 size={16} /></button>
            <button className="icon-btn" onClick={() => onDelete(d.id)} title="Excluir"><Trash2 size={16} /></button>
          </>
        )}
      </td>
    </tr>
  );
});

export default function Dispositivos() {
  const {
    devices,
    loadingInitial,
    isFetching,
    error,
    addDevice,
    updateDevice,
    removeDevice
  } = useDispositivos();

  // ----------------------- UI State -----------------------
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [orderBy, setOrderBy] = useState("name");
  const [asc, setAsc] = useState(true);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isAdmin, setIsAdmin] = useState(null);
  const [adminLoading, setAdminLoading] = useState(true);

  // ----------------------- Verifica se é admin (uma vez só) -----------------------
  useEffect(() => {
  setIsAdmin(false);
  setAdminLoading(false);
    

    /*
    let alive = true;
    (async () => {
      try {
        const nomeUsuario = JSON.parse(sessionStorage.getItem("user") || "{}")?.user;
        if (!nomeUsuario) {
          setIsAdmin(false);
          return;
        }
        const admin = await checkAdmin(nomeUsuario);
        if (alive) setIsAdmin(!!admin);
      } catch {
        if (alive) setIsAdmin(false);
      } finally {
        if (alive) setAdminLoading(false);
      }
    })();
    return () => { alive = false; };
  */
  }, []);

  // ----------------------- helper centralizado (estável) -----------------------
  const getDeviceStatus = useCallback((d) => {
    if (d.is_unstable || d.instability?.is_unstable) {
      return { icon: <AlertTriangle size={14} />, label: "Instável", cls: "unstable" };
    }
    if (d.last_ping?.alive) {
      return { icon: <Wifi size={14} />, label: "Online", cls: "on" };
    }
    return { icon: <WifiOff size={14} />, label: "Offline", cls: "off" };
  }, []);

  // ----------------------- Derived -----------------------
  const filteredDevices = useMemo(() => {
    return devices
      .filter((d) => {
        if (statusFilter === "ALL") return true;
        const st = getDeviceStatus(d).label; // Online / Offline / Instável
        if (statusFilter === "UNSTABLE") return st === "Instável";
        if (statusFilter === "ONLINE") return st === "Online";
        if (statusFilter === "OFFLINE") return st === "Offline";
        return true;
      })
      .filter(
        (d) =>
          d.name.toLowerCase().includes(search.toLowerCase()) ||
          d.ip.includes(search)
      )
      .sort((a, b) => {
        const av = a[orderBy];
        const bv = b[orderBy];
        if (typeof av === "string") {
          return asc ? av.localeCompare(bv) : bv.localeCompare(av);
        }
        return asc ? av - bv : bv - av;
      });
  }, [devices, search, statusFilter, orderBy, asc, getDeviceStatus]);

  // ----------------------- Handlers -----------------------
  const openCreate = () => {
    if (!isAdmin) return;
    setFormValues(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (device) => {
    if (!isAdmin) return;
    setFormValues(device);
    setEditingId(device.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!isAdmin) return;
    if (window.confirm("Tem certeza que deseja excluir este dispositivo?")) {
      await removeDevice(id);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    if (!isValidAddress(formValues.ip)) {
      alert("Endereço inválido: informe um IPv4 (ex. 192.168.1.10) ou um hostname válido.");
      return;
    }
    if (editingId) {
      await updateDevice(editingId, formValues);
    } else {
      await addDevice(formValues);
    }
    setShowForm(false);
  };

  const handleIpChange = useCallback(
    (e) =>
      setFormValues((prev) => ({
        ...prev,
        ip: formatAddress(e.target.value),
      })),
    []
  );

  // ----------------------- Render -----------------------
  if (error) return <p className="error-text">Erro ao carregar dispositivos 😢</p>;

  return (
    <div className="tela-container devices-page">
      {/* Filtros */}
      <div className="filters">
        <input
          placeholder="Buscar por nome ou IP"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* Filtro de status coerente com a lógica */}
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="ALL">Todos os status</option>
          <option value="ONLINE">Online</option>
          <option value="OFFLINE">Offline</option>
          <option value="UNSTABLE">Instável</option>
        </select>

        <select value={orderBy} onChange={(e) => setOrderBy(e.target.value)}>
          <option value="name">Ordenar por nome</option>
          <option value="ip">Ordenar por IP</option>
          <option value="active">Ordenar por status</option>
        </select>

        <button className="icon-btn" title="Alterar ordem" onClick={() => setAsc((a) => !a)}>
          {asc ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>

        {!adminLoading && isAdmin && (
        <button className="primary-btn" onClick={openCreate} title="Novo Dispositivo">
          <PlusCircle size={18} /> <span>Novo Dispositivo</span>
        </button>
        )}

        {/* badge sutil de atualização contínua (não desmonta a tabela) */}
        {isFetching && <span className="soft-spinner">Atualizando…</span>}
        {adminLoading && <span className="soft-spinner">Verificando permissões…</span>}

      </div>

      {/* Lista */}
      <div className="table-wrapper">
        {loadingInitial ? (
          <p>Carregando dispositivos...</p>
        ) : filteredDevices.length === 0 ? (
          <p>Nenhum dispositivo encontrado.</p>
        ) : (
          <table className="devices-table">
            <thead>
              <tr>
                <th>Dispositivo</th>
                <th>Endereço IP</th>
                <th>Descrição</th>
                <th>Status</th>
                <th>Latência (ms)</th>
                <th className="actions-col">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredDevices.map((d) => (
                <DeviceRow
                  key={d.id}
                  d={d}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  getDeviceStatus={getDeviceStatus}
                  isAdmin={!adminLoading && !!isAdmin}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de criação / edição */}
      {showForm && isAdmin && (
        <div className="modal">
          <form className="device-form" onSubmit={handleSubmit}>
            <h2>{editingId ? "Editar Dispositivo" : "Novo Dispositivo"}</h2>

            <label>
              Nome
              <input
                value={formValues.name}
                onChange={(e) => setFormValues({ ...formValues, name: e.target.value })}
                required
              />
            </label>

            <label>
              IP
              <input
                value={formValues.ip}
                onChange={handleIpChange}
                placeholder="IP ou domínio"
                inputMode="text"
                required
              />
            </label>

            <label>
              Descrição (opcional)
              <input
                value={formValues.description}
                onChange={(e) => setFormValues({ ...formValues, description: e.target.value })}
              />
            </label>

            <div className="actions">
              <button type="button" onClick={() => setShowForm(false)}>
                Cancelar
              </button>
              <button type="submit">Salvar</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
